import React from "react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import type { InsightsKpis } from "../hooks/useInsightsKpis";
import { formatFailRate } from "../utils/chartFormatters";
import {
  ATTENTION_FAIL_RATE_CRITICAL,
  ATTENTION_SCORE_THRESHOLD,
} from "../constants";
import { Skeleton } from "../../../ui/Skeleton";

interface KpiStripProps {
  kpis: InsightsKpis;
  loading?: boolean;
}

const compact = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const cardClass =
  "bg-card rounded-lg shadow-sm border border-border p-4 relative overflow-hidden";
const labelClass =
  "text-[11px] uppercase tracking-wide text-muted-foreground font-semibold";
const valueClass =
  "font-mono text-3xl font-semibold tabular-nums leading-none tracking-tight";

/** Week-over-week change chip: green up / red down / muted flat. */
function DeltaChip({ value }: { value: number }) {
  const flat = Math.abs(value) < 0.005;
  const cls = flat
    ? "text-muted-foreground"
    : value > 0
      ? "text-primary"
      : "text-destructive";
  const glyph = flat ? "▬" : value > 0 ? "▲" : "▼";
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-mono font-semibold tabular-nums ${cls}`}
      title="vs. the prior period"
    >
      {glyph} {Math.abs(value).toFixed(2)}
    </span>
  );
}

/**
 * Headline KPI strip at the top of /insights: the one-glance answer to "how's
 * the portfolio right now, and which way is it moving?" — overall score (with a
 * week-over-week delta), evaluation volume, results needing attention (matching
 * the triage rail), and the criterion that regressed most vs the prior period.
 */
export function KpiStrip({ kpis, loading = false }: KpiStripProps) {
  const {
    overallScore,
    overallDelta,
    evaluations,
    needsAttention,
    attentionItems,
    biggestRegression,
  } = kpis;

  if (loading) {
    return (
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="kpi-strip"
      >
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={cardClass}>
            <Skeleton height={12} width={90} className="rounded" />
            <Skeleton height={30} width={70} className="rounded mt-3" />
          </div>
        ))}
      </div>
    );
  }

  const alert = needsAttention > 0;

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      data-testid="kpi-strip"
    >
      {/* Overall score + WoW delta */}
      <div className={cardClass} data-testid="kpi-overall">
        <p className={labelClass}>Overall score</p>
        <div className="mt-2 flex items-end gap-2">
          <span className={valueClass}>
            {overallScore != null ? overallScore.toFixed(2) : "—"}
          </span>
          {overallDelta != null && <DeltaChip value={overallDelta} />}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          count-weighted mean, all criteria
        </p>
      </div>

      {/* Evaluations */}
      <div className={cardClass} data-testid="kpi-evaluations">
        <p className={labelClass}>Evaluations</p>
        <div className="mt-2">
          <span className={valueClass}>{compact.format(evaluations)}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2">in the selected range</p>
      </div>

      {/* Needs attention */}
      <div
        className={`${cardClass} ${alert ? "border-destructive/40" : ""}`}
        data-testid="kpi-attention"
      >
        {alert && (
          <span className="absolute left-0 top-0 bottom-0 w-1 bg-destructive" />
        )}
        <p className={labelClass}>Needs attention</p>
        <div className="mt-2">
          <span className={`${valueClass} ${alert ? "text-destructive" : ""}`}>
            {needsAttention}
          </span>
        </div>
        {alert ? (
          <ul className="mt-2 flex flex-col gap-1">
            {attentionItems.map((item) => (
              <li
                key={item.key}
                className="flex items-center gap-2 text-xs text-muted-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                <span className="truncate" title={item.label}>
                  {item.label}
                </span>
                <span className="font-mono tabular-nums text-destructive">
                  {item.failRate != null
                    ? formatFailRate(item.failRate)
                    : item.avg.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-primary mt-2 inline-flex items-center gap-1">
            <CheckCircleIcon className="h-3.5 w-3.5" /> nothing fails{" "}
            {ATTENTION_SCORE_THRESHOLD.toFixed(2)} more than{" "}
            {formatFailRate(ATTENTION_FAIL_RATE_CRITICAL)} of the time
          </p>
        )}
      </div>

      {/* Biggest regression vs the prior period */}
      <div className={cardClass} data-testid="kpi-regression">
        <p className={labelClass}>Biggest regression</p>
        <div className="mt-2">
          <span
            className={`${valueClass} ${biggestRegression ? "text-destructive" : ""}`}
          >
            {biggestRegression ? `−${biggestRegression.drop.toFixed(2)}` : "—"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-2 truncate">
          {biggestRegression
            ? `${biggestRegression.label} · vs. prior period`
            : "no declines vs. prior period"}
        </p>
      </div>
    </div>
  );
}
